import React from 'react';
import styled, { css } from 'styled-components';

import { PayByBank } from 'src/assets/pay-by-bank';
import BankLogosHorizontal from 'src/assets/bank-logos-horizontal';
import { Button } from 'components/core';

import { CashBackBadge } from 'src/payments/components/cash-back-badge/cash-back-badge';
import { useDutchiePay } from 'src/payments/hooks';
import { SignupButtonLocation } from 'src/payments/constants';
import type { ValidIncentiveValue } from 'src/payments/hooks/shared/incentive-types';

import { usePaySignUpButton } from './use-pay-sign-up-button';
import { WhatIsPayByBank } from '../what-is-pbb';

export type PayButtonPosition = 'bottom' | 'top';

export type PaySignUpButtonProps = {
  textPosition?: PayButtonPosition;
  cname?: string;
  goToCheckout?: () => void;
  refHref?: string;
  isMobile?: boolean;
  enrollmentSource?: string;
  location: SignupButtonLocation;
  includeSubtext?: boolean;
  buttonOverrides?: string;
  onSignUpButtonClick?: () => void;
  // Opt-in cash-back treatment: swaps the label for bank logos + a dynamic "$X back" badge.
  cashBackBadgeValue?: ValidIncentiveValue;
};

export const PaySignUpButton = ({
  textPosition = `bottom`,
  cname,
  goToCheckout,
  refHref,
  isMobile,
  enrollmentSource,
  location,
  includeSubtext = true,
  buttonOverrides = '',
  onSignUpButtonClick,
  cashBackBadgeValue,
}: PaySignUpButtonProps): JSX.Element => {
  const { isEnrolledDutchiePay, whiteLabelConfig } = useDutchiePay();
  const { handleClick } = usePaySignUpButton({
    location,
    goToCheckout,
    cname,
    refHref,
    enrollmentSource,
  });

  const hasCashBackValue = cashBackBadgeValue !== undefined;

  const handleSignUpButtonClick = (): void => {
    onSignUpButtonClick?.();
    handleClick();
  };

  return (
    <>
      <SignupButton
        data-testid='dutchie-pay-signup-button'
        onClick={handleSignUpButtonClick}
        aria-label='dutchiePay'
        isMobile={isMobile}
        custom={whiteLabelConfig.useWhiteLabel}
        overrides={buttonOverrides}
        $hasCashBackBadge={hasCashBackValue}
      >
        {hasCashBackValue ? (
          <>
            <BankLogosHorizontal aria-hidden />
            <span>Pay by Bank</span>
            <CashBackBadge value={cashBackBadgeValue} />
          </>
        ) : (
          <>
            <PayByBank />
            <span>Pay by bank instantly</span>
          </>
        )}
      </SignupButton>
      {!isEnrolledDutchiePay && includeSubtext && (
        <Subtext textPosition={textPosition} isMobile={isMobile}>
          <WhatIsPayByBank />
        </Subtext>
      )}
    </>
  );
};

export const SignupButton = styled<{ overrides; $hasCashBackBadge?: boolean }>(Button)`
  ${({ theme: { breakpoints, colors, spaces }, custom, overrides, $hasCashBackBadge }) => css`
    --font-family: ${custom ? `inherit` : `Matter, proxima-nova, sans-serif`};
    --font: normal 22px/27px var(--font-family);

    align-items: center;
    background-color: ${custom ? colors.customLinkColor : colors.dutchiePayGreen};
    border: none;
    color: ${colors.white};
    cursor: pointer;
    display: flex;
    font: var(--font);
    height: 45px;
    justify-content: center;
    text-transform: none;
    width: 100%;
    ${overrides}

    ${breakpoints.down('xs')} {
      font-size: 16px;
      height: 43px;
    }

    &&& {
      padding: 10px 15px;

      span {
        margin-right: 5px;
      }

      svg {
        margin-right: ${spaces[1]};
        vertical-align: baseline;
      }

      ${$hasCashBackBadge &&
      css`
        gap: 8px;

        span,
        svg {
          margin-right: 0;
        }
      `}
    }
  `}
`;

const Subtext = styled.div`
  ${({ theme: { breakpoints, colors, spaces } }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${spaces[2]};
    color: ${colors.grey[45]};
    font-size: 12px;
    margin-top: ${spaces[3]};

    ${breakpoints.down('xs')} {
      flex-direction: column;
    }
  `}
`;
